import React from 'react';
import i18n from 'i18n/config';
import colors from 'common/styles/colors';
import HealthIconSvg from 'common/images/HealthIconSvg';
import NoteSvgNew from 'common/images/NoteSvgNew';
import AlarmFlagSvg from 'common/images/AlarmFlagSvg';
import OverallAlarmWarningNew from 'common/images/OverallAlarmWarningNew';
import OverallAlarmCriticalNew from 'common/images/OverallAlarmCriticalNew';
import MoteUnreachableNew from 'common/images/MoteUnreachableNew';
import MachineSvg from 'common/images/MachineSvg';
import NewBatteryIcon from 'common/images/NewBatteryIcon';
import { Task, Wrench } from 'common/images/FaIcons';
import { humanize } from 'common/helpers/humanize';

export const getSortOptions = (t = i18n.t.bind(i18n)) => ([
  {
    value: {
      field: 'timestamp',
      direction: 'desc'
    },
    text: t('activity.constants.sort.dateNewest', { defaultValue: 'Date - newest' })
  }, {
    value: {
      field: 'timestamp',
      direction: 'asc'
    },
    text: t('activity.constants.sort.dateOldest', { defaultValue: 'Date - oldest' })
  }
]);

// Deprecated: prefer getSortOptions(t).
export const sortOptions = getSortOptions();

export const getTaskSortOptions = (t = i18n.t.bind(i18n)) => ([
  {
    value: {
      field: 'condition'
    },
    text: t('activity.constants.sort.taskPriority', { defaultValue: 'Task Priority' })
  }
]);

export const taskSortOptions = getTaskSortOptions();

export const getDayOptions = (t = i18n.t.bind(i18n)) => ([
  {
    value: 1,
    text: t('activity.constants.days.one', { defaultValue: '1 Day' })
  }, {
    value: 7,
    text: t('activity.constants.days.seven', { defaultValue: '7 Days' })
  }, {
    value: 30,
    text: t('activity.constants.days.thirty', { defaultValue: '30 Days' })
  }, {
    value: null,
    text: t('activity.constants.days.all', { defaultValue: 'All' })
  }
]);

export const dayOptions = getDayOptions();

export const VIEW_BY_GROUPED = 'grouped';
export const VIEW_BY_INDIVIDUAL = 'individual';

// Pre-i18n builds persisted the display text ('Individual' / 'Group by Asset')
// in localStorage instead of the value constants. Map those legacy values so a
// stale localStorage entry keeps the user's prior intent.
const LEGACY_VIEW_BY = {
  Individual: VIEW_BY_INDIVIDUAL,
  'Group by Asset': VIEW_BY_GROUPED,
};

// Coerce any persisted/legacy/unknown view_by value to a canonical constant.
// Unknown or missing values default to Individual (matches getInitialSearchParams).
export const normalizeViewBy = (value) => {
  if (value === VIEW_BY_GROUPED || value === VIEW_BY_INDIVIDUAL) return value;
  if (LEGACY_VIEW_BY[value]) return LEGACY_VIEW_BY[value];
  return VIEW_BY_INDIVIDUAL;
};

export const getInitialSearchParams = (t = i18n.t.bind(i18n)) => {
  const sortOptions = getSortOptions(t);
  const dayOptions = getDayOptions(t);
  return {
    order_by: sortOptions[0],
    search_key: '',
    type: [],
    days: dayOptions[3],
    state: ['open', 'in_progress', 'scheduled', 'review_pending'],
    event_type: [],
    machines: [],
    read_status: [],
    analysis: [],
    suspension_status: ['off'],
    defect: [],
    has_cost_saved: false,
    has_defect: false,
    transient: false,
    view_by: VIEW_BY_INDIVIDUAL,
    // Event replay: 'live' | 'replayed' narrows events by origin.
    source: [],
  };
};

// Deprecated: prefer getInitialSearchParams(t).
export const initialSearchParams = getInitialSearchParams();

export const getSiteLevelSortOptions = (t = i18n.t.bind(i18n)) => ([
  ...getSortOptions(t),
  {
    value: {
      field: 'machine_criticality',
      direction: 'desc'
    },
    text: t('activity.constants.sort.machineCriticality', { defaultValue: 'Machine Criticality' })
  },
  {
    value: {
      field: 'machines',
      direction: 'asc'
    },
    text: t('activity.constants.sort.machines', { defaultValue: 'Machines' })
  }
]);

export const siteLevelSortOptions = getSiteLevelSortOptions();

export const getTypeOptions = (t = i18n.t.bind(i18n)) => ([
  { text: t('activity.constants.type.event', { defaultValue: 'Event' }), value: 'event' },
  { text: t('activity.constants.type.note', { defaultValue: 'Note' }), value: 'note' },
  { text: t('activity.constants.type.diagnosis', { defaultValue: 'Diagnosis' }), value: 'diagnosis' },
  { text: t('activity.constants.type.ahsFeedback', { defaultValue: 'AHS Feedback' }), value: 'ahs_feedback' },
  { text: t('activity.constants.type.task', { defaultValue: 'Task' }), value: 'task' },
  { text: t('activity.constants.type.repair', { defaultValue: 'Repair' }), value: 'repair' },
]);

export const typeOptions = getTypeOptions();

// Label for one key in a group header's type breakdown. Events break down by
// TEMPLATE name, which has no translation and is humanised; the five activity
// types do have one, and lifting the grouped view's filter pin is what let
// them reach a header in the first place. Without this a German user read
// "Ahs Feedback: 3" and "Task: 2" in English.
export const getActivityTypeLabel = (t, key) => {
  const option = getTypeOptions(t).find(o => o.value === key);
  return option ? option.text : humanize(key);
};

export const getAnalysisOptions = (t = i18n.t.bind(i18n)) => ([
  { text: t('activity.constants.analysis.analysed', { defaultValue: 'Analysed' }), value: 'analysed' },
  { text: t('activity.constants.analysis.notAnalysed', { defaultValue: 'Not Analysed' }), value: 'not_analysed' },
]);

export const analysisOptions = getAnalysisOptions();

export const getSourceOptions = (t = i18n.t.bind(i18n)) => ([
  { text: t('activity.constants.source.live', { defaultValue: 'Live' }), value: 'live' },
  { text: t('activity.constants.source.replayed', { defaultValue: 'Replayed' }), value: 'replayed' },
]);

export const getStateOptions = (t = i18n.t.bind(i18n)) => ([
  { text: t('activity.constants.state.open', { defaultValue: 'Open' }), value: 'open' },
  { text: t('activity.constants.state.inProgress', { defaultValue: 'In Progress' }), value: 'in_progress' },
  { text: t('activity.constants.state.scheduled', { defaultValue: 'Scheduled' }), value: 'scheduled' },
  { text: t('activity.constants.state.reviewPending', { defaultValue: 'Review Pending' }), value: 'review_pending' },
  { text: t('activity.constants.state.closed', { defaultValue: 'Closed' }), value: 'closed' },
]);

export const stateOptions = getStateOptions();

export const getReadStatusOptions = (t = i18n.t.bind(i18n)) => ([
  { text: t('activity.constants.readStatus.read', { defaultValue: 'Read' }), value: 'read' },
  { text: t('activity.constants.readStatus.unread', { defaultValue: 'Unread' }), value: 'unread' }
]);

export const readStatusOptions = getReadStatusOptions();

export const getSuspensionStatusOptions = (t = i18n.t.bind(i18n)) => ([
  { text: t('activity.constants.suspensionStatus.on', { defaultValue: 'On' }), value: 'on' },
  { text: t('activity.constants.suspensionStatus.off', { defaultValue: 'Off' }), value: 'off' }
]);

export const suspensionStatusOptions = getSuspensionStatusOptions();

export const dateFormat = 'MMM Do, YYYY';

export const getViewByOptions = (t = i18n.t.bind(i18n)) => ([
  {
    text: t('activity.constants.view.groupByAsset', { defaultValue: 'Group by Asset' }),
    value: VIEW_BY_GROUPED
  }, {
    text: t('activity.constants.view.individual', { defaultValue: 'Individual' }),
    value: VIEW_BY_INDIVIDUAL
  }
]);

export const viewByOptions = getViewByOptions();

export const activitiesInOrder = [
  'task', 'repair',
  'machine_condition_critical',
  'vibration_critical', 'temperature_critical',
  'vibration_acceleration_critical',
  'vibration_demod_critical',
  'health_score_critical',
  'machine_condition_warning',
  'vibration_warning',
  'temperature_warning',
  'vibration_demod_warning',
  'vibration_acceleration_warning',
  'vibration_envelope_alarm',
  'current_envelope_alarm',
  'health_score_warning',
  'battery_level_critical',
  'battery_level_warning',
  'tx_battery_level_critical',
  'tx_battery_level_warning',
  'mote_unreachable',
  'tx_unreachable',
  'note',
  'diagnosis',
  // Lifting the grouped view's filter pin let non-event types reach a group
  // header. Without an entry here ahs_feedback still counted towards Total but
  // rendered no chip of its own, so the breakdown did not add up.
  'ahs_feedback'
];

// Overall alarms with no fixed place (admin-created) follow the fixed order.
export const chipsInOrder = counted => activitiesInOrder
  .map(eventType => counted.find(item => item.event_type === eventType))
  .filter(Boolean)
  .concat(counted.filter(item => item.is_overall && activitiesInOrder.indexOf(item.event_type) === -1));

export const getActivityIcon = (activityArrayWithCount) => {
  const highestCriticalEvent = activityArrayWithCount.reduce((highestEvent, currentEvent) => {
    if (currentEvent.count > highestEvent.count && currentEvent.event_type.includes('critical')) {
      return currentEvent;
    }
    return highestEvent;
  }, { count: 0 });
  const highestCountEvent = activityArrayWithCount.reduce((highestEvent, currentEvent) => {
    if (currentEvent.count > highestEvent.count) {
      return currentEvent;
    }
    return highestEvent;
  }, { count: 0 });

  const shown = (highestCriticalEvent.count !== 0 && !highestCriticalEvent.event_type.includes('battery_level')) ? highestCriticalEvent : highestCountEvent;
  const { event_type } = shown;
  switch (event_type) {
    case 'vibration_warning':
    case 'vibration_demod_warning':
    case 'vibration_acceleration_warning':
    {
      return <OverallAlarmWarningNew />;
    }
    case 'vibration_critical':
    case 'vibration_demod_critical':
    case 'vibration_acceleration_critical':
    case 'vibration_envelope_alarm':
    case 'current_envelope_alarm': {
      return <OverallAlarmCriticalNew />;
    }
    case 'machine_condition_warning': {
      return <MachineSvg fill={colors.warning} />;
    }
    case 'machine_condition_critical': {
      return <MachineSvg fill={colors.critical} />;
    }
    case 'tx_unreachable': {
      return <AlarmFlagSvg fill={colors.alarmCritical} />;
    }
    case 'battery_level_warning':
    case 'battery_level_critical':
    case 'tx_battery_level_warning':
    case 'tx_battery_level_critical': {
      return <NewBatteryIcon fill={event_type.includes('critical') ? '#FF4F39' : '#F0C000'} />;
    }
    case 'mote_unreachable': {
      return <MoteUnreachableNew fill={colors.critical} />;
    }
    // AHS feedback is health-score work, the same family as a diagnosis.
    case 'diagnosis':
    case 'ahs_feedback': {
      return <HealthIconSvg />;
    }
    case 'task': {
      return <Task />;
    }
    case 'repair': {
      return <Wrench />;
    }
    case 'note': {
      return <NoteSvgNew />;
    }
    default: {
      if (shown.is_overall) {
        return event_type.includes('critical') ? <OverallAlarmCriticalNew /> : <OverallAlarmWarningNew />;
      }
      if (event_type) {
        return <AlarmFlagSvg fill={event_type.includes('critical') ? colors.alarmCritical : colors.alarmWarning} />;
      }
      return '';
    }
  }
};

export const getActivityTextColor = (text) => {
  switch (text) {
    case 'mote_unreachable':
    case 'tx_unreachable': {
      return colors.darkGray;
    }
    case 'diagnosis':
    case 'ahs_feedback':
    case 'task':
    case 'repair':
    case 'note': {
      return colors.black;
    }
    default: {
      if (text.includes('critical')) return colors.critical;
      if (text.includes('warning') || text === 'vibration_envelope_alarm' || text === 'current_envelope_alarm') return colors.warning;
      if (text) return colors.alarmWarning;
      return colors.black;
    }
  }
};
